import { useMemo } from "react";
import type { CountryHoursEntry } from "../../../../domain/models/metrics";
import { useLocalization } from "../../../../hooks/useLocalization";
import { CountryMetricRow } from "../types";
import { buildCountryMetricRows } from "../utils/countryMetricRows";

interface UseCountryHoursDataResult {
  rows: CountryMetricRow[];
  isEmpty: boolean;
}

/**
 * Display rows for the "Hours in VR per country" card.
 *
 * `totalHours` is the same-filter hours total from the activity chart API; when
 * it exceeds the sum of the individually-listed countries, the difference is the
 * aggregate the backend privacy-masked to 0 and belongs to "Other".
 *
 * See `buildCountryMetricRows` for the bucketing, sorting and percentage rules.
 */
export const useCountryHoursData = (
  rawData: CountryHoursEntry[],
  totalHours?: number,
  isUserLevelDataEnabled = false,
): UseCountryHoursDataResult => {
  const { t } = useLocalization();

  const rows = useMemo(
    () =>
      buildCountryMetricRows({
        entries: rawData.map((entry) => ({
          country: entry.country,
          value: entry.hoursInVr,
        })),
        total: totalHours,
        revealSmallValues: isUserLevelDataEnabled,
        otherLabel: t("pages.dataAnalytics.internalUsage.countryUsage.other"),
        unknownLabel: t(
          "pages.dataAnalytics.internalUsage.countryUsage.unknown",
        ),
      }),
    [rawData, totalHours, t, isUserLevelDataEnabled],
  );

  return {
    rows,
    isEmpty: rows.length === 0,
  };
};
